import React, { Component, Fragment } from "react";
import empty from "is-empty";
import moment from "moment";
import { confirmAlert } from "react-confirm-alert";

import {
  TextField,
  Modal,
  Button,
  InputAdornment,
  TablePagination,
} from "@material-ui/core";
import { Close, Done, Edit, ArrowUpward, ArrowDownward } from "@material-ui/icons";

import {
  sa_add_project_task,
  sa_delete_project_task,
} from "../../store/actions/peripherals/springahead";
import { db_update_task } from "../../store/actions/tables/client_projects";

import DatePicker from "../Inputs/DatePicker";

// Pure sort so it can be unit-tested without rendering. Missing values (no bill
// date, no amount) always sort last — in BOTH directions — rather than riding a
// sentinel that flips to the top when descending. Never mutates the source.
export function sortTasks(data, field, asc) {
  const isMissing = (t) => {
    switch (field) {
      case "ESTIMATED_BILL_DATE":
        return empty(t.ESTIMATED_BILL_DATE);
      case "BillRate":
        return empty(t.BillRate);
      case "Name":
        return empty(t.Name);
      // ACTUAL_BILL_DATE: an empty value is real data ("NO"), not missing.
      default:
        return false;
    }
  };
  const val = (t) => {
    switch (field) {
      case "ESTIMATED_BILL_DATE":
        return moment.utc(t.ESTIMATED_BILL_DATE);
      case "BillRate":
        return Number(t.BillRate);
      case "ACTUAL_BILL_DATE":
        return empty(t.ACTUAL_BILL_DATE) ? 0 : 1;
      case "Name":
      default:
        return String(t.Name || "").toLowerCase();
    }
  };
  return [...data].sort((a, b) => {
    const am = isMissing(a);
    const bm = isMissing(b);
    if (am && bm) return 0;
    if (am) return 1; // missing rows sink to the bottom regardless of direction
    if (bm) return -1;
    const av = val(a);
    const bv = val(b);
    if (av < bv) return asc ? -1 : 1;
    if (av > bv) return asc ? 1 : -1;
    return 0;
  });
}

class TaskPopup extends Component {
  constructor(props) {
    super(props);
    this.state = {
      task: {
        Id: "",
        Name: "",
        BillRate: "",
        ESTIMATED_BILL_DATE: "",
      },
      newRow: false,
      editId: "",
      error: "",
      page: 0,
      // Default: estimated bill date, earliest first.
      sortField: "ESTIMATED_BILL_DATE",
      sortAsc: true,
    };
  }

  // Click a column to sort by it; click the active column again to flip
  // direction. Reset to page 0 and cancel any in-progress edit (the same guard
  // the pager uses) so the Done button can't fire against a row that reordered.
  handleSort = (field) => {
    this.setState((s) => ({
      sortField: field,
      sortAsc: s.sortField === field ? !s.sortAsc : true,
      page: 0,
      // Cancel an in-progress EDIT so Done can't fire against a reordered row,
      // but never wipe an in-progress ADD draft (newRow uses the same task
      // state) — resorting mid-entry must not erase what the user typed.
      editId: "",
      task: s.newRow
        ? s.task
        : { Id: "", Name: "", BillRate: "", ESTIMATED_BILL_DATE: "" },
    }));
  };

  sortHeader = (label, field, extraClass) => {
    const active = this.state.sortField === field;
    const ariaSort = active
      ? this.state.sortAsc
        ? "ascending"
        : "descending"
      : "none";
    return (
      <th scope="col" className={`table-title ${extraClass || ""}`} aria-sort={ariaSort}>
        {/* native <button> for keyboard focus + Enter/Space activation */}
        <button
          type="button"
          onClick={() => this.handleSort(field)}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            font: "inherit",
            color: "inherit",
            cursor: "pointer",
            userSelect: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.15rem",
          }}
        >
          {label}
          {active &&
            (this.state.sortAsc ? (
              <ArrowUpward style={{ fontSize: "1rem" }} />
            ) : (
              <ArrowDownward style={{ fontSize: "1rem" }} />
            ))}
        </button>
      </th>
    );
  };

  // 10 tasks per page — short enough that the list never needs a scrollbar.
  // The Add and Close buttons live in the fixed footer (not the table), so
  // they're always on-screen regardless of task count (the original bug was
  // that Add was buried at the bottom of a long, unscrollable list).
  static ROWS_PER_PAGE = 10;

  // Keep the current page in range as the task list grows/shrinks (e.g. after a
  // delete) so state.page never strands the user on a now-out-of-range page.
  componentDidUpdate() {
    const maxPage = Math.max(
      0,
      Math.ceil(this.props.data.length / TaskPopup.ROWS_PER_PAGE) - 1
    );
    if (this.state.page > maxPage) this.setState({ page: maxPage });
  }

  addTask = async () => {
    const n = this.state.task.Name;
    const t = {
      ...this.state.task,
      ProjectId: this.props.project.SPRINGAHEAD_ID,
      Name: `${this.props.project.CLIENT.ABBREV}${
        this.props.project.TYPE === "Fixed Price" ? " MS" : ""
      } - ${n} (${this.props.project.SPRINGAHEAD_ID})`,
    };
    const resp = await sa_add_project_task(t);
    if (resp.complete) {
      this.props.addTask({ ...t, Id: resp.Id, InnerTaskId: resp.InnerTaskId });
      this.setState({
        ...this.state,
        task: { Name: "", BillRate: "", ESTIMATED_BILL_DATE: "" },
        newRow: false,
        error: "",
      });
    } else {
      this.setState({ ...this.state, error: resp.error });
    }
  };

  removeTask = async (task) => {
    const resp = await sa_delete_project_task({
      ...task,
      ProjectId: this.props.project.SPRINGAHEAD_ID,
    });
    if (resp.complete) {
      this.props.removeTask(task);
      this.setState({ ...this.state, newRow: false, error: "" });
    } else {
      this.setState({ ...this.state, error: resp.error });
    }
  };

  updateTask = async () => {
    const { task } = this.state;
    const resp = await db_update_task(task);
    if (resp.complete) {
      const existing = this.props.data.filter((t) => t.Id === task.Id)[0];
      console.log(existing);
      this.props.removeTask(task);
      this.props.addTask({
        ...existing,
        ESTIMATED_BILL_DATE: task.ESTIMATED_BILL_DATE,
      });
      this.setState({
        ...this.state,
        editId: "",
        task: { Id: "", Name: "", BillRate: "", ESTIMATED_BILL_DATE: "" },
      });
    } else {
      this.setState({ ...this.state, error: resp.error });
    }
  };

  confirmRemove = (task) => {
    confirmAlert({
      title: "Confirm Task Removal",
      message:
        "Are you sure you want to remove this task. This has billing implications.",
      buttons: [
        {
          label: "Yes",
          onClick: () => this.removeTask(task),
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  render() {
    const { task, newRow, editId, error } = this.state;
    const { data, project } = this.props;

    const rowsPerPage = TaskPopup.ROWS_PER_PAGE;
    const pageCount = Math.max(1, Math.ceil(data.length / rowsPerPage));
    // Clamp so removing tasks (which shrinks data) can't leave us on a now-empty page.
    // Display value only (re-derived every render) — do NOT setState this; the
    // source of truth is this.state.page, kept in range by componentDidUpdate.
    const displayPage = Math.min(this.state.page, pageCount - 1);
    // Sort the whole list before paginating, so page 1 shows the earliest
    // bill dates rather than the first slice of the server's order.
    const sortedData = sortTasks(data, this.state.sortField, this.state.sortAsc);
    const pageData = sortedData.slice(
      displayPage * rowsPerPage,
      displayPage * rowsPerPage + rowsPerPage
    );

    let list = pageData.map((item) => (
      <tr key={item.Id}>
        <td data-container="body" className="table-data">
          {item.Name}
        </td>
        {project.TYPE === "Fixed Price" && (
          <Fragment>
            <td data-container="body" className="table-data">
              {empty(item.BillRate) ? "Not Specified" : `$${item.BillRate}`}
            </td>
            {(empty(editId) || editId !== item.Id) && (
              <td data-container="body" className="table-data">
                {empty(item.ESTIMATED_BILL_DATE)
                  ? ""
                  : moment.utc(item.ESTIMATED_BILL_DATE).format("YYYY-MM-DD")}
              </td>
            )}
            {!empty(editId) && editId === item.Id && (
              <DatePicker
                value={task.ESTIMATED_BILL_DATE}
                onChange={(value: any) =>
                  this.setState({
                    ...this.state,
                    task: {
                      ...task,
                      ESTIMATED_BILL_DATE: empty(value)
                        ? ""
                        : moment.utc(value).format("YYYY-MM-DD"),
                    },
                  })
                }
              />
            )}
            <td data-container="body" className="table-data">
              {empty(item.ACTUAL_BILL_DATE) ? "NO" : "YES"}
            </td>
          </Fragment>
        )}
        <th
          data-container="body"
          className="table-data"
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <div
            style={{
              cursor: "pointer",
              height: "1.7rem",
              width: "1.7rem",
              borderRadius: "50%",
              background: "red",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => this.confirmRemove(item)}
          >
            <Close style={{ fontSize: "1rem", color: "white" }} />
          </div>
          {!newRow && empty(editId) && project.TYPE === "Fixed Price" && (
            <div
              style={{
                cursor: "pointer",
                height: "1.7rem",
                width: "1.7rem",
                borderRadius: "50%",
                background: "grey",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: "0.25rem",
              }}
              onClick={() =>
                this.setState({
                  ...this.state,
                  editId: item.Id,
                  task: {
                    ...task,
                    Id: item.Id,
                    ESTIMATED_BILL_DATE: item.ESTIMATED_BILL_DATE,
                  },
                })
              }
            >
              <Edit style={{ fontSize: "1rem", color: "white" }} />
            </div>
          )}
          {!newRow && !empty(editId) && project.TYPE === "Fixed Price" && (
            <div
              style={{
                cursor: "pointer",
                height: "1.7rem",
                width: "1.7rem",
                borderRadius: "50%",
                background: empty(task.ESTIMATED_BILL_DATE)
                  ? "lightgrey"
                  : "green",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: "0.25rem",
              }}
              onClick={
                empty(task.ESTIMATED_BILL_DATE)
                  ? () => {}
                  : () => this.updateTask()
              }
            >
              <Done style={{ fontSize: "1rem", color: "white" }} />
            </div>
          )}
        </th>
      </tr>
    ));

    // list.push((<tr key="00000"><td colSpan="3" style={{fontSize: "1rem", color: "orange"}}>*Tasks can be used to enter time as a sub-project level. Contact accounting to add tasks to your project.</td></tr>))

    return (
      <Modal
        open
        onClose={this.props.onHide}
        style={{
          display: "flex",
          height: "100vh",
          width: "100vw",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            background: "white",
            padding: "1rem",
            fontSize: "1.2rem",
            display: "flex",
            flexDirection: "column",
            borderRadius: "6px",
            minWidth: "30rem",
            maxHeight: "85vh",
            overflowY: "auto",
          }}
        >
          <div
            className="project-table"
            style={{ marginBottom: "2rem", maxHeight: "60vh", overflowY: "auto" }}
          >
            <table className="table" style={{ marginBottom: 0 }}>
              <thead>
                <tr className="table-head">
                  {this.sortHeader("Task", "Name", "leftrow")}
                  {project.TYPE === "Fixed Price" && (
                    <Fragment>
                      {this.sortHeader("Amount", "BillRate")}
                      {this.sortHeader("Date", "ESTIMATED_BILL_DATE")}
                      {this.sortHeader("Billed", "ACTUAL_BILL_DATE")}
                    </Fragment>
                  )}
                  <th scope="col" className="table-title status"></th>
                </tr>
              </thead>
              <tbody className="table-body">
                {list}
                {newRow && (
                  <tr key={-2}>
                    <td data-container="body" className="table-data">
                      <TextField
                        type="text"
                        variant="outlined"
                        inputProps={{
                          style: { padding: "0.5rem 0" },
                          maxLength:
                            128 -
                            `${project.CLIENT.ABBREV}${
                              project.TYPE === "Fixed Price" ? " MS" : ""
                            } -- (${project.SPRINGAHEAD_ID})`.length,
                        }}
                        maxLength="5"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              style={{ padding: 0 }}
                            >
                              {project.CLIENT.ABBREV}
                              {project.TYPE === "Fixed Price" ? " MS" : ""} -
                            </InputAdornment>
                          ),
                        }}
                        style={{ minWidth: "20rem", width: "100%" }}
                        value={task.Name}
                        onChange={(e) =>
                          this.setState({
                            ...this.state,
                            task: { ...task, Name: e.target.value },
                          })
                        }
                      />
                    </td>
                    {project.TYPE === "Fixed Price" && (
                      <Fragment>
                        <td
                          data-container="body"
                          className="table-data"
                          style={{ maxWidth: "10rem" }}
                        >
                          <TextField
                            type="number"
                            variant="outlined"
                            inputProps={{ style: { padding: "0.5rem 0" } }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment
                                  position="start"
                                  style={{ padding: 0 }}
                                >
                                  $
                                </InputAdornment>
                              ),
                            }}
                            value={task.BillRate}
                            onChange={(e) =>
                              this.setState({
                                ...this.state,
                                task: { ...task, BillRate: e.target.value },
                              })
                            }
                          />
                        </td>
                        <td
                          data-container="body"
                          className="table-data"
                          style={{ width: "15rem" }}
                        >
                          <DatePicker
                            value={task.ESTIMATED_BILL_DATE}
                            onChange={(value: any) =>
                              this.setState({
                                ...this.state,
                                task: {
                                  ...task,
                                  ESTIMATED_BILL_DATE: empty(value)
                                    ? ""
                                    : moment.utc(value).format("YYYY-MM-DD"),
                                },
                              })
                            }
                          />
                        </td>
                        <td
                          data-container="body"
                          className="table-data"
                          style={{ maxWidth: "10rem" }}
                        >
                          NO
                        </td>
                      </Fragment>
                    )}
                    <th
                      data-container="body"
                      className="table-data"
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                      }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        style={{
                          background: empty(task.Name) ? "lightgrey" : "green",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: empty(task.Name) ? "black" : "white",
                        }}
                        onClick={empty(task.Name) ? () => {} : this.addTask}
                      >
                        SAVE
                      </Button>
                    </th>
                  </tr>
                )}
                {!empty(error) && <span style={{ color: "red" }}>{error}</span>}
              </tbody>
            </table>
          </div>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "1rem",
            }}
          >
            {data.length > rowsPerPage ? (
              <TablePagination
                component="div"
                count={data.length}
                // clamped display value, NOT this.state.page (the source of truth,
                // kept in range by componentDidUpdate) — never setState this prop
                page={displayPage}
                onChangePage={(_e, newPage) =>
                  // Changing pages cancels any in-progress edit so the Done
                  // button can't fire updateTask against a now-off-screen row.
                  this.setState({
                    ...this.state,
                    page: newPage,
                    editId: "",
                    task: { Id: "", Name: "", BillRate: "", ESTIMATED_BILL_DATE: "" },
                  })
                }
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
              />
            ) : (
              <span />
            )}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              {!newRow && empty(editId) && (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => this.setState({ ...this.state, newRow: true })}
                >
                  Add
                </Button>
              )}
              <Button
                onClick={this.props.onHide}
                variant="contained"
                style={{ backgroundColor: "red", color: "white" }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    );
  }
}

export default TaskPopup;
